<!-- ExpireOverdueResultModal.vue -->
<script setup>
import { onMounted, onBeforeUnmount, watch } from "vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, default: false },
  // ExpireOverdueResultDto: { totalExpired, affectedUsers: [{ userName, email, subscriptionId }] }
  result: { type: Object, required: true },
});

const emit = defineEmits(["close"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. O componente não desmonta entre
// aberturas (só alterna `show`), então o watch recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

function handleEscKey(event) {
  if (!props.show) return;
  if (event.key === "Escape") emit("close");
}

onMounted(() => document.addEventListener("keydown", handleEscKey));
onBeforeUnmount(() => document.removeEventListener("keydown", handleEscKey));
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-backdrop" @click.self="emit('close')">
      <div ref="panelRef" class="modal-panel" :style="panelStyle">
        <!-- HEADER -->
        <div
          class="modal-header"
          :class="{
 'modal-header--draggable': isDraggable,
            'modal-header--dragging': isDragging,
          }"
          @pointerdown="onHandlePointerDown"
        >
          <h3 class="modal-title">Resultado da Expiração</h3>
          <button
            @click="emit('close')"
            type="button"
            class="modal-close-btn"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <!-- BODY -->
        <div class="modal-body">
          <!-- Resumo -->
          <div
            class="summary-block"
            :class="
 result.totalExpired > 0
                ? 'summary-block--warn'
                : 'summary-block--ok'
            "
          >
            <svg
              v-if="result.totalExpired > 0"
              class="summary-icon"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else
              class="summary-icon summary-icon--ok"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>

            <div>
              <p class="summary-title" v-if="result.totalExpired > 0">
                {{ result.totalExpired }}
                {{
                  result.totalExpired === 1
                    ? "assinatura expirada"
                    : "assinaturas expiradas"
                }}
              </p>
              <p class="summary-title" v-else>
                Nenhuma assinatura vencida encontrada.
              </p>
              <p class="summary-subtitle" v-if="result.totalExpired > 0">
                Os usuários abaixo foram movidos para o plano gratuito.
              </p>
            </div>
          </div>

          <!-- Lista de afetados -->
          <div v-if="result.affectedUsers?.length > 0" class="users-list-wrap">
            <table class="users-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>E-mail</th>
                  <th>SubId</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="user in result.affectedUsers"
                  :key="user.subscriptionId"
                >
                  <td>{{ user.userName }}</td>
                  <td>{{ user.email }}</td>
                  <td class="td-center">{{ user.subscriptionId }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="emit('close')"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 36rem;
  min-height: 420px;
}

/* Bloco de resumo */
.summary-block {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid;
}

.summary-block--warn {
  background-color: var(--warning-bg);
  border-color: var(--warning-bg);
  color: var(--warning-text);
}

.summary-block--ok {
  background-color: var(--success-bg);
  border-color: #a7f3d0;
  color: var(--success-text);
}

.summary-icon {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  color: var(--warning);
  margin-top: 0.1rem;
}

.summary-icon--ok {
  color: #10b981;
}

.summary-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
}

.summary-subtitle {
  font-size: 0.82rem;
  margin: 0.2rem 0 0;
  opacity: 0.8;
}

/* Tabela de usuários afetados */
.users-list-wrap {
  border: 1px solid var(--border-color);
  border-radius: 10px;
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.users-table thead tr {
  background-color: var(--bg-header);
}

.users-table th {
  padding: 0.45rem 0.75rem;
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  color: var(--text-dark);
  font-weight: 600;
  white-space: nowrap;
}

.users-table tbody tr {
  border-top: 1px solid var(--border-color);
}

.users-table tbody tr:hover {
  background-color: var(--bg-active-nav);
}

.users-table td {
  padding: 0.4rem 0.75rem;
  color: var(--text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.td-center {
  text-align: center;
}

</style>